<script setup>
/**
 * MyYearWidget
 * Shows 12-month performance overview for the current year.
 * Agents see their own data; admins get an agent selector.
 * Admin click opens fullscreen modal with all agents grid.
 */
import { ref, computed, watch, onMounted } from 'vue'
import { useAuthStore } from '@app/store/auth.js'
import { useStatistics } from '@core/composables/useStatistics.js'

const MONTH_LABELS = [
  'ינו',
  'פבר',
  'מרץ',
  'אפר',
  'מאי',
  'יונ',
  'יול',
  'אוג',
  'ספט',
  'אוק',
  'נוב',
  'דצמ',
]

const authStore = useAuthStore()
const { getAgentYearlyArchive, getAllAgentsAnalyses } = useStatistics()

const isLoading = ref(true)
const yearData = ref([]) // 12 elements, null = no data
const agents = ref([])
const selectedAgentId = ref(null)

const currentYear = new Date().getFullYear()

const isAdmin = computed(() => authStore.isAdmin)

// Modal state
const showModal = ref(false)
const modalLoading = ref(false)
const allAgentsYearData = ref([]) // [{ id, name, months: [...12] }]

function getColorClass(analys) {
  if (analys < 50) return 'my-year__val--low'
  if (analys <= 80) return 'my-year__val--mid'
  return 'my-year__val--high'
}

async function loadYearData(agentId) {
  if (!agentId) return
  isLoading.value = true
  try {
    yearData.value = await getAgentYearlyArchive(agentId, currentYear)
  } catch {
    yearData.value = Array(12).fill(null)
  } finally {
    isLoading.value = false
  }
}

async function openModal() {
  if (!isAdmin.value) return
  showModal.value = true
  modalLoading.value = true
  try {
    const results = await Promise.all(
      agents.value.map(async (agent) => {
        const months = await getAgentYearlyArchive(String(agent.id), currentYear).catch(() =>
          Array(12).fill(null)
        )
        return { id: agent.id, name: agent.name, months }
      })
    )
    allAgentsYearData.value = results
  } catch {
    allAgentsYearData.value = []
  } finally {
    modalLoading.value = false
  }
}

function closeModal() {
  showModal.value = false
}

watch(selectedAgentId, (id) => {
  if (id) loadYearData(id)
})

onMounted(async () => {
  try {
    if (isAdmin.value) {
      const list = await getAllAgentsAnalyses()
      agents.value = (list || [])
        .filter((a) => a.analys > 0)
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'he'))
      if (agents.value.length) {
        selectedAgentId.value = String(agents.value[0].id)
      }
    } else {
      const agentId = authStore.userAgentId
      if (agentId) {
        selectedAgentId.value = String(agentId)
        await loadYearData(selectedAgentId.value)
      }
    }
  } catch {
    /* silent */
  } finally {
    if (!isAdmin.value) isLoading.value = false
  }
})
</script>

<template>
  <div class="my-year__root">
    <div class="my-year" @click="openModal">
      <div class="my-year__header">
        <span class="my-year__title">השנה שלי</span>
        <select
          v-if="isAdmin && agents.length"
          v-model="selectedAgentId"
          class="my-year__select"
          @click.stop
        >
          <option v-for="a in agents" :key="a.id" :value="String(a.id)">
            {{ a.name }}
          </option>
        </select>
        <span v-if="isAdmin" class="my-year__expand-hint" @click.stop="openModal">
          לחץ להכל
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <polyline points="15 3 21 3 21 9" />
            <line x1="21" y1="3" x2="14" y2="10" />
            <polyline points="9 21 3 21 3 15" />
            <line x1="3" y1="21" x2="10" y2="14" />
          </svg>
        </span>
      </div>

      <!-- Loading -->
      <div v-if="isLoading" class="my-year__loading">
        <div class="my-year__spinner" />
      </div>

      <!-- Month grid -->
      <div v-else class="my-year__grid">
        <div v-for="(entry, idx) in yearData" :key="idx" class="my-year__col">
          <span v-if="entry" class="my-year__val" :class="getColorClass(entry.analys)"
            >{{ Math.round(entry.analys) }}%</span
          >
          <span v-else class="my-year__val my-year__val--empty">—</span>
          <span class="my-year__month">{{ MONTH_LABELS[idx] }}</span>
        </div>
      </div>
    </div>

    <!-- Fullscreen Modal (admin only) -->
    <Teleport to="body">
      <Transition name="my-year-modal">
        <div v-if="showModal" class="mym-overlay" @click.self="closeModal">
          <div class="mym-panel">
            <div class="mym-header">
              <h2 class="mym-title">השנה שלי — כל הסוכנים ({{ currentYear }})</h2>
              <button class="mym-close" @click="closeModal">
                <svg
                  viewBox="0 0 24 24"
                  width="22"
                  height="22"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <!-- Modal loading -->
            <div v-if="modalLoading" class="mym-loading">
              <div class="my-year__spinner" />
              <span>טוען נתונים...</span>
            </div>

            <!-- Agents grid -->
            <div v-else class="mym-grid">
              <div v-for="agent in allAgentsYearData" :key="agent.id" class="mym-card">
                <span class="mym-card__name">{{ agent.name }}</span>
                <div class="my-year__grid">
                  <div v-for="(entry, idx) in agent.months" :key="idx" class="my-year__col">
                    <span v-if="entry" class="my-year__val" :class="getColorClass(entry.analys)"
                      >{{ Math.round(entry.analys) }}%</span
                    >
                    <span v-else class="my-year__val my-year__val--empty">—</span>
                    <span class="my-year__month">{{ MONTH_LABELS[idx] }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.my-year__root {
  display: flex;
  flex-direction: column;
}

.my-year {
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-md);
  direction: rtl;
  overflow: hidden;
}

.my-year__header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: 12px;
}

.my-year__title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--text-secondary);
  letter-spacing: 0.02em;
}

.my-year__select {
  font-size: 12px;
  padding: var(--spacing-xs) var(--spacing-sm);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  background: var(--bg-primary);
  color: var(--text-primary);
  outline: none;
  max-width: 130px;
  direction: rtl;
}

.my-year__select:focus {
  border-color: var(--color-primary);
}

.my-year__expand-hint {
  margin-inline-start: auto;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: 11px;
  color: var(--text-muted);
  cursor: pointer;
  transition: color var(--transition-fast);
}

.my-year__expand-hint:hover {
  color: var(--color-primary);
}

.my-year__expand-hint svg {
  flex-shrink: 0;
}

.my-year__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 70px;
  color: var(--text-muted);
}

.my-year__spinner {
  width: 24px;
  height: 24px;
  border: 2px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: my-year-spin 0.8s linear infinite;
}

@keyframes my-year-spin {
  to {
    transform: rotate(360deg);
  }
}

.my-year__grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: var(--spacing-xs);
  text-align: center;
}

.my-year__col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 6px var(--spacing-xs);
  background: var(--bg-secondary);
  border-radius: var(--border-radius-sm);
  min-width: 0;
}

.my-year__val {
  font-size: 12px;
  font-weight: var(--font-weight-bold);
  line-height: 1.3;
}

.my-year__val--low {
  color: var(--color-error);
}

.my-year__val--mid {
  color: var(--color-warning);
}

.my-year__val--high {
  color: var(--color-success);
}

.my-year__val--empty {
  color: var(--text-muted);
  font-weight: var(--font-weight-normal);
}

.my-year__month {
  font-size: 10px;
  color: var(--text-muted);
  line-height: 1.2;
}

/* ============================================
   Fullscreen Modal
   ============================================ */
.mym-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  direction: rtl;
}

.mym-panel {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  width: 95vw;
  max-width: 1200px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
}

.mym-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md) var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
  position: sticky;
  top: 0;
  background: var(--bg-primary);
  z-index: 1;
}

.mym-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0;
}

.mym-close {
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px;
  border-radius: var(--border-radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-fast);
}

.mym-close:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.mym-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-xl);
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

.mym-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
}

.mym-card {
  background: var(--bg-secondary);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
}

.mym-card__name {
  display: block;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin-bottom: var(--spacing-xs);
  text-align: center;
}

/* Modal card inner grid uses bg-tertiary to contrast with card bg */
.mym-card .my-year__col {
  background: var(--bg-tertiary);
}

/* Modal transition */
.my-year-modal-enter-active,
.my-year-modal-leave-active {
  transition: opacity 0.2s ease;
}

.my-year-modal-enter-active .mym-panel,
.my-year-modal-leave-active .mym-panel {
  transition: transform 0.2s ease;
}

.my-year-modal-enter-from,
.my-year-modal-leave-to {
  opacity: 0;
}

.my-year-modal-enter-from .mym-panel,
.my-year-modal-leave-to .mym-panel {
  transform: scale(0.95);
}

/* Responsive */
@media (max-width: 1024px) {
  .mym-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .my-year {
    padding: var(--spacing-sm) 12px;
  }

  .my-year__grid {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .my-year__col {
    min-width: 32px;
  }

  .mym-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--spacing-sm);
    padding: var(--spacing-md);
  }

  .mym-panel {
    width: 100vw;
    max-height: 100vh;
    border-radius: 0;
  }
}

@media (max-width: 480px) {
  .mym-grid {
    grid-template-columns: 1fr;
  }
}
</style>
